import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // 90s timeout — long enough for slowest LLM endpoints (Avatar Blueprint
  // batch ~75s wall-clock), short enough so a stuck request doesn't keep
  // the spinner running forever.
  timeout: 90000,
});

// Track auth-revoked redirect so we don't spam toasts on cascade calls.
let _accessRevokedHandled = false;

// ── Tester preview írás-tiltó request guard ────────────────────────
// Amikor admin Tester preview módban van (`sessionStorage.mex.previewAsTester
// === "1"`), MINDEN write művelet (POST/PUT/PATCH/DELETE) blokkolt, kivéve
// az auth és preview-toggle endpoint-okat. Így preview módban garantáltan
// NEM lehet módosítani: az admin saját workspace-ét, más user account-ot,
// Aktív alapot, Tudástárat, identitásokat, generált anyagokat. Teljes
// user-flow teszteléshez külön demo tester account szükséges.
const PREVIEW_WRITE_ALLOW_PREFIXES = [
  "/auth/",     // login/logout endpoint-ok
  "/me",        // self identity
];

api.interceptors.request.use((config) => {
  try {
    if (sessionStorage.getItem("mex.previewAsTester") !== "1") return config;
  } catch { return config; }
  const method = (config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return config;
  const url = String(config.url || "");
  for (const p of PREVIEW_WRITE_ALLOW_PREFIXES) {
    if (url.startsWith(p)) return config;
  }
  // Block: friendly toast + canceled request promise.
  toast.info(
    "Tester nézet — előnézet módban a műveletek le vannak tiltva. Lépj vissza admin nézetbe a Tester banner gombbal.",
    { id: "preview-readonly", duration: 4500 }
  );
  return Promise.reject(new axios.Cancel("preview-readonly"));
});

// Global response interceptor. Handles 3 cases:
//   1. 429 / 403 with structured detail {kind, message}  →  human-friendly toast (limit / agent-lock)
//   2. 403 with plain "Access revoked" detail            →  user has been deactivated mid-session:
//                                                            show inactive-account toast + redirect to landing
//   3. 409 with structured detail {code: "missing_alap"} →  alap-not-found toast + auto-reset local active alap
//   4. anything else                                     →  propagate untouched
// CRITICAL: when `detail` is an OBJECT (cases 1, 3), we OVERWRITE
// `err.response.data.detail` with the human-readable .message string
// BEFORE re-throwing. Downstream call sites that do
// `toast.error(e?.response?.data?.detail || "fallback")` would otherwise
// crash with „Objects are not valid as a React child" — this single
// normalisation guarantees every caller receives a safe string.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const s = err?.response?.status;
    const d = err?.response?.data?.detail;

    // Case 1: structured limit / lock detail
    if ((s === 429 || s === 403) && d && typeof d === "object" && d.message) {
      // user-facing only, NO technical detail
      toast.error(d.message, { id: `limit-${d.kind || s}`, duration: 5000 });
      try { err.response.data.detail = String(d.message); } catch { /* noop */ }
      return Promise.reject(err);
    }

    // Case 1b: 409 missing_alap — the user picked an Aktív alap that was
    // since deleted. Backend refuses to silently fall back; surface a clear
    // Hungarian toast, AUTO-RESET the stale localStorage pointer so a
    // simple page refresh recovers, and propagate a safe string detail.
    if (s === 409 && d && typeof d === "object" && d.code === "missing_alap") {
      const msg = d.message || "A kiválasztott aktív alap már nem létezik, kérlek válassz újat.";
      toast.error(msg, { id: `missing-alap-${d.agent_type || ""}`, duration: 6000 });
      try {
        const RAW = localStorage.getItem("mex_active_alap");
        if (RAW) {
          const obj = JSON.parse(RAW) || {};
          const layer = d.agent_type;
          if (layer && (layer === "voice" || layer === "client" || layer === "offer")) {
            if (obj[layer] && obj[layer] === d.missing_id) {
              obj[layer] = null;
              localStorage.setItem("mex_active_alap", JSON.stringify(obj));
            }
          }
        }
      } catch { /* swallow — best-effort cleanup */ }
      try { err.response.data.detail = msg; } catch { /* noop */ }
      return Promise.reject(err);
    }

    // Case 2: plain-string 403 ("Access revoked") — admin deactivated this
    // user while they were still logged in. Show human message + kick out.
    if (s === 403 && typeof d === "string" && d.toLowerCase().includes("access revoked")) {
      if (!_accessRevokedHandled) {
        _accessRevokedHandled = true;
        toast.error(
          "A hozzáférésed jelenleg nem aktív. Ha szerinted ez tévedés, kérlek írj nekünk.",
          { id: "access-revoked", duration: 6500 }
        );
        // Defer to let toast render, then hard-redirect to landing so any
        // in-flight cookie state is wiped on the next mount.
        setTimeout(() => {
          window.location.href = "/?denied=inactive";
        }, 1100);
      }
      return Promise.reject(err);
    }

    // Final safety net: if `detail` is still a non-string object (any other
    // structured error we don't recognise), coerce to .message-or-fallback
    // string so no downstream code can render the raw object.
    if (d && typeof d === "object") {
      try {
        err.response.data.detail = String(d.message || "Hiba történt.");
      } catch { /* noop */ }
    }

    return Promise.reject(err);
  }
);
